import { lazy, Suspense, useEffect, useState } from "react";
import "tailwindcss/dist/base.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "styles/globalStyles.css";
import { HelmetProvider } from "react-helmet-async";
import logoLoader from "images/Loader/logoLoader.png";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useCartContext } from "context/UseCartContext.js";
import Header from "./components/headers/header.js";
import Footer from "./components/footers/footer.js";
import { login } from "./api/LoginApi.js";
import { getHeaderConfig, setBearerToken, setBranchId } from "./utility/HeaderConfig.js";
import { branchdetails } from "api/BranchDetailsApi";
import { getBranchId, setCorporateId } from "utility/HeaderConfig";
import ScrollToTop from "utility/ScrollToTop.js";

// ── Lazy-loaded pages (code-split by route) ──────────────────────────────────
const MainLandingPage        = lazy(() => import("./MainLandingPage"));
const ProductList            = lazy(() => import("./pages/ProductList.js"));
const MyScheme               = lazy(() => import("./pages/MyScheme.js"));
const About                  = lazy(() => import("./pages/About.js"));
const JoinScheme             = lazy(() => import("./pages/JoinScheme.js"));
const SchemeList             = lazy(() => import("./pages/SchemeList.js"));
const ProductDetails         = lazy(() => import("./pages/ProductDetails.js"));
const SchemeDetails          = lazy(() => import("./pages/SchemeDetails.js"));
const WishList               = lazy(() => import("./pages/WishList.js"));
const MostLiked              = lazy(() => import("./pages/MostLiked.js"));
const BookAnAppointment      = lazy(() => import("./pages/BookAnAppointment.js"));
const Offers                 = lazy(() => import("./pages/Offers"));
const NewArrivals            = lazy(() => import("./pages/NewArrivals.js"));
const CartPage               = lazy(() => import("./pages/CartPage.js"));
const CheckoutPage           = lazy(() => import("pages/CheckoutPage.js"));
const Contact                = lazy(() => import("pages/Contact.js"));
const TermsConditions        = lazy(() => import("pages/Policies/TermsConditions"));
const PrivacyPolicy          = lazy(() => import("pages/Policies/PrivacyPolicy"));
const ShippingPolicy         = lazy(() => import("pages/Policies/ShippingPolicy"));
const RefundPolicy           = lazy(() => import("pages/Policies/RefundPolicy"));
const OrderStatusPage        = lazy(() => import("pages/OrderStatusPage"));
const ReferralPoints         = lazy(() => import("pages/ReferralPoints.js"));
const ProductOrderDetails    = lazy(() => import("./pages/ProductOrderDetails.js"));
const BestSellingComponent   = lazy(() => import("components/BestSellingComponent.js"));
const TrendOnShopComponent   = lazy(() => import("components/TrendOnShopComponent.js"));
const ShopOurExclusiveComponent = lazy(() => import("components/ShopOurExclusiveComponent.js"));
const OccasionCategoriesComponent = lazy(() => import("components/OccasionCategoriesComponent.js"));
const MyPurchase             = lazy(() => import("components/MyPurchase.js"));
const AddAddress             = lazy(() => import("components/AddAddress.js"));
const ViewAddressPage        = lazy(() => import('./pages/ViewAddressPage.js'));
const TechWallet             = lazy(() => import("components/TechWallet.js"));

// Minimal page-level fallback — no layout shift, invisible spinner
const PageFallback = () => (
  <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid #f0ddb0", borderTopColor: "#b8860b", animation: "spin 0.7s linear infinite" }} />
  </div>
);

const CONFIG_IMAGES = JSON.parse(
  '{"PGIMAGES":[{"key":"paymentGatway","value":"https://images.prod-rasiinfotech.in/ecomm/paymentGatway.png"}],"STORIMAGES":[{"key":"store1","value":"https://images.prod-rasiinfotech.in/ecomm/jewelleryShop1.jpg"},{"key":"store2","value":"https://images.prod-rasiinfotech.in/ecomm/jewelleryShop2.jpg"}],"MOBILE_APP_QR":[{"key":"qrCode","value":"https://images.prod-rasiinfotech.in/ecomm/downloadApps.png"}],"E_COMMERCE_BANNERS":[{"key":"cart","value":"https://images.prod-rasiinfotech.in/ecomm/cart.png"},{"key":"enquiry","value":"https://images.prod-rasiinfotech.in/ecomm/enquiry.png"},{"key":"joinScheme","value":"https://images.prod-rasiinfotech.in/ecomm/joinScheme.png"},{"key":"mostLiked","value":"https://images.prod-rasiinfotech.in/ecomm/mostLiked.png"},{"key":"newArrivals","value":"https://images.prod-rasiinfotech.in/ecomm/newArrivals.png"},{"key":"offer","value":"https://images.prod-rasiinfotech.in/ecomm/offer.png"},{"key":"privacyPolicy","value":"https://images.prod-rasiinfotech.in/ecomm/privacyPolicy.png"},{"key":"productDetails","value":"https://images.prod-rasiinfotech.in/ecomm/productDetails.png"},{"key":"productList","value":"https://images.prod-rasiinfotech.in/ecomm/productList.png"},{"key":"refundPolicy","value":"https://images.prod-rasiinfotech.in/ecomm/refundPolicy.png"},{"key":"savingScheme","value":"https://images.prod-rasiinfotech.in/ecomm/savingScheme.png"},{"key":"schemeDetails","value":"https://images.prod-rasiinfotech.in/ecomm/schemeDetails.png"},{"key":"shippingPolicy","value":"https://images.prod-rasiinfotech.in/ecomm/shippingPolicy.png"},{"key":"termsConditions","value":"https://images.prod-rasiinfotech.in/ecomm/termsConditions.png"},{"key":"whishList","value":"https://images.prod-rasiinfotech.in/ecomm/whishList.png"},{"key":"paymentGatway","value":"https://images.prod-rasiinfotech.in/ecomm/paymentGatway.png"},{"key":"contact","value":"https://images.prod-rasiinfotech.in/ecomm/contact.png"},{"key":"referral","value":"https://images.prod-rasiinfotech.in/ecomm/whishList.png"}]}'
);

export default function App() {
  const [token, setToken] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [branch, setBranch] = useState({});
  const configImages = CONFIG_IMAGES;
  const { branchId } = useCartContext();
  const helmetContext = {};

  const toggleVisibility = () => setIsVisible(window.pageYOffset > 300);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  useEffect(() => {
    initialAuth();
    window.addEventListener("scroll", toggleVisibility, { passive: true });
    return () => window.removeEventListener("scroll", toggleVisibility);
    // eslint-disable-next-line
  }, []);

  const initialAuth = () => {
    login(JSON.stringify({ username: "ONLINEPAYMENT", password: "Payment@123" })).then((data) => {
      if (data?.authStatus?.toLowerCase() === "success" && data.token) {
        setBearerToken(data.token);
        setBranchId(data.empDefaultBranch);
        setToken(true);
        branchDetails();
      }
    });
  };

  useEffect(() => { branchDetails(); }, [branchId]); // eslint-disable-line

  const branchDetails = () => {
    branchdetails(getBranchId()).then((data) => {
      setBranch(data.response);
      setCorporateId(data.response.storeDetails);
    });
  };

  return (
    <HelmetProvider context={helmetContext}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      {token && getHeaderConfig() ? (
        <Router>
          <ScrollToTop />
          <Header branch={branch} />
          <main id="main-content" tabIndex="-1" style={{outline:"none"}}>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/"                          element={<MainLandingPage />} />
              <Route path="/MyScheme"                  element={<MyScheme />} />
              <Route path="/ProductList"               element={<ProductList configImages={configImages} />} />
              <Route path="/SchemeList"                element={<SchemeList branch={branch} configImages={configImages} />} />
              <Route path="/ProductDetails/:id"        element={<ProductDetails branch={branch} configImages={configImages} />} />
              <Route path="/SchemeDetails"             element={<SchemeDetails configImages={configImages} />} />
              <Route path="/About"                     element={<About branch={branch} />} />
              <Route path="/JoinScheme"                element={<JoinScheme configImages={configImages} />} />
              <Route path="/WishList"                  element={<WishList configImages={configImages} />} />
              <Route path="/MostLiked"                 element={<MostLiked configImages={configImages} />} />
              <Route path="/NewArrivals"               element={<NewArrivals configImages={configImages} />} />
              <Route path="/BookAnAppointment"         element={<BookAnAppointment configImages={configImages} />} />
              <Route path="/Offers"                    element={<Offers configImages={configImages} />} />
              <Route path="/CartPage"                  element={<CartPage configImages={configImages} />} />
              <Route path="/CheckoutPage"              element={<CheckoutPage />} />
              <Route path="/Contact"                   element={<Contact configImages={configImages} />} />
              <Route path="/TermsConditions"           element={<TermsConditions branch={branch} configImages={configImages} />} />
              <Route path="/PrivacyPolicy"             element={<PrivacyPolicy branch={branch} configImages={configImages} />} />
              <Route path="/ShippingPolicy"            element={<ShippingPolicy branch={branch} configImages={configImages} />} />
              <Route path="/RefundPolicy"              element={<RefundPolicy branch={branch} configImages={configImages} />} />
              <Route path="/OrderStatusPage"           element={<OrderStatusPage />} />
              <Route path="/ReferralPoints"            element={<ReferralPoints configImages={configImages} />} />
              <Route path="/ProductOrderDetails"       element={<ProductOrderDetails configImages={configImages} />} />
              <Route path="/BestSellingComponent"      element={<BestSellingComponent configImages={configImages} />} />
              <Route path="/TrendOnShopComponent"      element={<TrendOnShopComponent configImages={configImages} />} />
              <Route path="/ShopOurExclusiveComponent" element={<ShopOurExclusiveComponent configImages={configImages} />} />
              <Route path="/OccasionCategoriesComponent" element={<OccasionCategoriesComponent configImages={configImages} />} />
              <Route path="/MyPurchase"                element={<MyPurchase configImages={configImages} />} />
              <Route path="/AddAddress"                element={<AddAddress configImages={configImages} />} />
              <Route path="/ViewAddress"               element={<ViewAddressPage />} />
              <Route path="/TechWallet"                element={<TechWallet configImages={configImages} branch={branch} />} />
            </Routes>
          </Suspense>
          </main>
          <Footer branch={branch} configImages={configImages} />
        </Router>
      ) : (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff" }}>
          <div style={{ position: "relative", width: "96px", height: "96px", flexShrink: 0, alignSelf: "center" }}>
            <div style={{
              position: "absolute", top: 0, left: 0,
              width: "96px", height: "96px",
              borderRadius: "50%",
              border: "3px solid #f0ddb0",
              borderTopColor: "#b8860b",
              animation: "spin 1s linear infinite",
              boxSizing: "border-box",
            }} />
            <img
              src={logoLoader}
              alt="Loading"
              style={{
                position: "absolute",
                top: "50%", left: "50%",
                transform: "translate(-50%, -50%)",
                width: "56px", height: "56px",
                maxWidth: "56px",
                objectFit: "contain",
                borderRadius: "50%",
                display: "block",
              }}
            />
          </div>
        </div>
      )}

      {/* WhatsApp FAB */}
      <a
        href="https://api.whatsapp.com/send?phone=+919169168787&text=I want to have a videocall for my custom jewel requirement"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: "fixed", bottom: isVisible ? 90 : 24, right: 20, zIndex: 999,
          width: 48, height: 48, borderRadius: "50%",
          background: "#25d366",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 16px rgba(37,211,102,0.5)",
          textDecoration: "none", transition: "bottom 0.3s",
        }}
        title="Chat on WhatsApp"
      >
        <svg viewBox="0 0 24 24" width="26" height="26" fill="#fff" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {isVisible && (
        <button
          onClick={scrollToTop}
          aria-label="Scroll to top"
          style={{
            position: "fixed", bottom: 24, right: 20, zIndex: 998,
            width: 44, height: 44, borderRadius: "50%",
            background: "#1c1c1e", border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
            transition: "background 0.2s, transform 0.2s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#b8860b"; e.currentTarget.style.transform = "translateY(-2px)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#1c1c1e"; e.currentTarget.style.transform = "translateY(0)"; }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="19" x2="12" y2="5"/>
            <polyline points="5 12 12 5 19 12"/>
          </svg>
        </button>
      )}
    </HelmetProvider>
  );
}